/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   media bizo Matterport selectors
 */

import type { Media } from '@zcode/bizos'
import { memoizeSelector } from '@zcode/memoizers'
import { compose } from 'redux'
import { logger } from '../../../../logger'

export const selectMatterportId = memoizeSelector((media: Media | undefined) => {
  const { isIframe, originUrl } = media || {}
  const isMatterport = originUrl?.includes?.('matterport') ?? null
  if (isIframe && isMatterport && originUrl) {
    try {
      const { searchParams } = new URL(originUrl)
      const id = searchParams.get('m') || searchParams.get('model')
      if (id) {
        return id
      }
      return null
    } catch (err) {
      logger.error('Bad matterport url', err)
      return null
    }
  }

  return null
})

export const selectMatterportUrl = memoizeSelector((media: Media | undefined) => {
  const { isIframe, originUrl } = media || {}
  const isMatterport = originUrl?.includes?.('matterport') ?? null
  if (isIframe && isMatterport && originUrl) {
    try {
      const { searchParams } = new URL(originUrl)
      const id = searchParams.get('m') || searchParams.get('model')
      if (!id) {
        return null
      }
      searchParams.set('play', '1')
      // sanitized origin url plus all search params:
      return `https://my.matterport.com/show/?${searchParams.toString()}`
    } catch (err) {
      logger.error('Bad matterport url', err)
      return null
    }
  }

  return null
})

export const selectMatterportImage = compose(
  (id) => (id ? `https://my.matterport.com/api/v1/player/models/${id}/thumb` : null),
  selectMatterportId
)

export const selectMatterportThumbnail = compose(
  (image) => (image ? `${image}?width=66&height=66` : null),
  selectMatterportImage
)

export const selectMatterportStandardImage = compose(
  (image) => (image ? `${image}?width=640&height=480` : null),
  selectMatterportImage
)

export const selectMatterportMaxResImage = compose(
  (image) => (image ? `${image}?width=1280&height=960` : null),
  selectMatterportImage
)
